import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CreditCard, FileText, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { PatientPaymentForm } from "@/components/patients/PatientPaymentForm";
import { useNavigate } from "react-router-dom";
import { Patient } from "@/types";

interface PatientQuickActionsProps {
  patientId: string;
  handleAddService: () => void;
  handlePaymentUpdate: (amount: number, paymentMode: 'cash' | 'card' | 'online') => void;
  handleStatusChange?: (status: string) => void;
  isDoctor: boolean;
  hasPrescriptions: boolean;
  patient: Patient;
}

export function PatientQuickActions({ 
  patientId, 
  handleAddService, 
  handlePaymentUpdate,
  handleStatusChange,
  isDoctor, 
  hasPrescriptions,
  patient,
}: PatientQuickActionsProps) {
  const navigate = useNavigate();
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);

  const handleCreatePrescription = () => {
    navigate(`/e-prescription/${patientId}`);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle>Quick Actions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <Button className="w-full" onClick={() => navigate(-1)}>
          Return to Queue
        </Button>
        <Button 
          className="w-full" 
          variant="outline"
          onClick={handleAddService}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Service
        </Button>
        
        {/* Only show Record Payment button for non-doctors */}
        {!isDoctor && (
          <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
            <DialogTrigger asChild>
              <Button className="w-full" variant="outline">
                <CreditCard className="mr-2 h-4 w-4" />
                Record Payment
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Record Patient Payment</DialogTitle>
                <DialogDescription>
                  Enter the payment amount received from the patient
                </DialogDescription>
              </DialogHeader>
              <PatientPaymentForm 
                patientId={patientId}
                onPaymentUpdate={handlePaymentUpdate}
                patient={patient}
                onClose={() => setIsPaymentDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>
        )}
        
        {/* Status dropdown - only for non-doctors */}
        {!isDoctor && handleStatusChange && (
          <>
            <style>{`
              .status-select-trigger {
                position: relative;
              }
              .status-select-trigger > span:first-child {
                position: absolute;
                left: 0;
                right: 2rem;
                text-align: center;
              }
              .status-select-item {
                justify-content: center;
              }
              .status-select-item > span:last-child {
                text-align: center;
                margin-left: auto;
                margin-right: auto;
                padding-left: 2rem;
              }
            `}</style>
            <Select
              value={patient.status}
              onValueChange={handleStatusChange}
            >
              <SelectTrigger className="w-full status-select-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="waiting" className="status-select-item">Waiting</SelectItem>
                <SelectItem value="in-consultation" className="status-select-item">In Consultation</SelectItem>
                <SelectItem value="completed" className="status-select-item">Completed</SelectItem>
                <SelectItem value="no-show" className="status-select-item">No Show</SelectItem>
              </SelectContent>
            </Select>
          </>
        )}
        
        {isDoctor && (
          <Button 
            className="w-full" 
            variant="default"
            onClick={handleCreatePrescription}
          >
            <FileText className="mr-2 h-4 w-4" />
            {hasPrescriptions ? "New Prescription" : "Create Prescription"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
